import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import './App.css';
import { fetchCurrentUser } from './api';
import { ChangelogProvider } from './components/Changelog';
// Logger removed - using inline conditions for production builds

/* Layouts */
import Sidebar from './layouts/Sidebar/sidebar';
import MobileSidebarToggle from './layouts/Sidebar/MobileSidebarToggle';
import AccountHeader from './layouts/Header/AccountHeader';
import { AccountShellProvider } from './contexts/AccountShellContext';

/* Pages */
import SignIn from './pages/Registration/signin';
import SignUp from './pages/Registration/signup';
import ForgotPassword from './pages/Registration/ForgotPassword';
import ResetPassword from './pages/Registration/ResetPassword';
import WidgetConversationsChat from './components/Assistant/WidgetConversationsChat';
import NotificationsDrawerContent from './components/header/NotificationsDrawerContent';
import TrustPage from './pages/Trust/TrustPage';
import AppRoutes from './app/AppRoutes';
import { saveProfileBackTarget } from './utils/profileNavigation';
import { classNames, teams } from './app/sidebarConfig';
import {
  getAppModeFromPath,
  stripAppModeFromPath,
  inferAppModeFromPath,
  withAppModePath,
  isAdminPath
} from './app/appMode';
import { clearSuperAdminCache } from './features/analytics/auth/useIsSuperAdmin';


import {
  Cog6ToothIcon,
  Squares2X2Icon,
  DocumentTextIcon,
  InboxIcon,
  CpuChipIcon,
  CircleStackIcon,
  MicrophoneIcon
} from '@heroicons/react/24/outline';

const CONVERSATIONS_DRAWER_OPEN_KEY = 'conversations_drawer_open';
export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const modeFromPath = useMemo(() => getAppModeFromPath(location.pathname), [location.pathname]);
  const normalizedPath = useMemo(() => stripAppModeFromPath(location.pathname), [location.pathname]);
  const activeAppMode = useMemo(
    () => modeFromPath || inferAppModeFromPath(location.pathname),
    [location.pathname, modeFromPath]
  );
  const { t, i18n, ready } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('directus_token') || '');
  const [trialEnded, setTrialEnded] = useState(false);
  const [trialEnds, setTrialEnds] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [userFirstName, setUserFirstName] = useState('');
  const [isConversationsDrawerOpen, setIsConversationsDrawerOpen] = useState(() => {
    try {
      return localStorage.getItem(CONVERSATIONS_DRAWER_OPEN_KEY) === '1';
    } catch (error) {
      return false;
    }
  });
  const [isNotificationsDrawerOpen, setIsNotificationsDrawerOpen] = useState(false);

  const handleSignOut = useCallback(() => {
    localStorage.clear();
    clearSuperAdminCache();
    setToken('');
    setIsAuthenticated(false);
    navigate('/signin');
  }, [navigate]);

  // All hooks must be called before any conditional rendering
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    try {
      localStorage.setItem(
        CONVERSATIONS_DRAWER_OPEN_KEY,
        isConversationsDrawerOpen ? '1' : '0'
      );
    } catch (error) {
      // ignore storage write errors
    }
  }, [isConversationsDrawerOpen]);

  useEffect(() => {
    if (location.pathname === '/profile') return;
    saveProfileBackTarget(`${location.pathname}${location.search}${location.hash}`);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const checkAuthentication = async () => {
      setIsAuthenticated(!!token);

      if (!token && !['/signin', '/signup', '/forgot-password', '/reset-password', '/security'].includes(location.pathname)) {
        navigate('/signin');
      } else if (token) {
        try {
          const userData = await fetchCurrentUser(['*', 'language']);

          if (userData) {
            localStorage.setItem('user_id', userData.id);
            const firstName = userData.first_name || '';
            setUserFirstName(firstName);
            localStorage.setItem('username', firstName);
            process.env.NODE_ENV === "development" && console.log('App.js: Full user data:', userData);
            
            // Set language from user profile
            if (userData.language) {
              const userLanguage = userData.language.split('-')[0]; // Convert 'en-US' to 'en'
              process.env.NODE_ENV === "development" && console.log('App.js: Setting language from user profile:', userLanguage);
              i18n.changeLanguage(userLanguage);
              localStorage.setItem('language', userLanguage);
            } else {
              process.env.NODE_ENV === "development" && console.log('App.js: No language found in user profile, using default');
            }
            
            // Check trial status
            const userTrialEnds = userData.trial_ends ? new Date(userData.trial_ends) : null;
            const isTrialEnded = userTrialEnds ? new Date() > userTrialEnds : false;
            
            // Check subscription status
            const userSubscriptionStatus = userData.subscription_status || 'none';
            // Mirrored from billing-core; past_due keeps access during Stripe's payment retry window.
            const hasActiveSubscription = ['active', 'trialing', 'past_due'].includes(userSubscriptionStatus);
            
            // Store states
            setTrialEnded(isTrialEnded);
            setTrialEnds(userTrialEnds);
            setSubscriptionStatus(userSubscriptionStatus);
            setSubscriptionPlan(userData.subscription_plan || null);
            
            // Redirect logic: allow access if either trial is active or subscription is active
            const needsPayment = isTrialEnded && !hasActiveSubscription;
            
            if (needsPayment && 
                !['/pricing', '/profile', '/subscription-success', '/'].includes(location.pathname) &&
                !isAdminPath(location.pathname)) {
              navigate('/pricing');
              return;
            }

            // Rest of your existing authentication logic
            const onboardingStatus = userData.onboarding_completed;
            const onboardingRoute = withAppModePath(activeAppMode, '/onboarding');

            if (!onboardingStatus && normalizedPath !== '/onboarding' && normalizedPath !== '/' && !isAdminPath(location.pathname)) {
              navigate(onboardingRoute);
            }
          }
        } catch (error) {
          process.env.NODE_ENV === "development" && console.error('Error fetching user details:', error);
          handleSignOut();
        }
      }
    };

    checkAuthentication();
    const tokenValidationInterval = setInterval(checkAuthentication, 60 * 1000);
    return () => clearInterval(tokenValidationInterval);
  }, [token, location.pathname, navigate, handleSignOut, i18n, activeAppMode, normalizedPath]);

  const handleSignIn = async (newToken) => {
    localStorage.setItem('directus_token', newToken);  // Save the token first
    // Decode token to get expiry time. The snippet below assumes the JWT token is structured as three Base64Url strings separated by "."
    const jwtPayload = JSON.parse(atob(newToken.split('.')[1]));
    const tokenExpiry = jwtPayload.exp; //timestamp when the token will expire 

    localStorage.setItem("directus_token_expiry", tokenExpiry)

    setToken(newToken);
    setIsAuthenticated(true);
    clearSuperAdminCache();

    try {
      const userData = await fetchCurrentUser();

      if (userData) {
        localStorage.setItem('user_id', userData.id);
        const firstName = userData.first_name || '';
        setUserFirstName(firstName);
        localStorage.setItem('username', firstName);

        navigate('/');
      } else {
        throw new Error('Failed to fetch user details');
      }
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching user details:', error);
      // Handle error, e.g., by logging out the user
      handleSignOut();
    }
  };

  // All useMemo hooks must also be before conditional rendering
  const navigation = useMemo(() => {
    if (activeAppMode === 'agent') {
      return [
        {
          name: t('navigation.dashboardShort'),
          href: withAppModePath('agent', '/dashboard'),
          icon: Squares2X2Icon,
          current: normalizedPath === '/dashboard' || normalizedPath === '/'
        },
        {
          name: t('navigation.inbox'),
          href: withAppModePath('agent', '/assistants'),
          icon: InboxIcon,
          current: normalizedPath === '/assistants' || normalizedPath.startsWith('/chat')
        },
        {
          name: t('navigation.aiAgent'),
          href: withAppModePath('agent', '/ai-agents'),
          icon: CpuChipIcon,
          current: normalizedPath === '/ai-agents' || normalizedPath.startsWith('/ai-agents/')
        },
        {
          name: t('navigation.datasets'),
          href: withAppModePath('agent', '/datasets'),
          icon: CircleStackIcon,
          current: normalizedPath.startsWith('/datasets')
        }
      ];
    }

    return [
      {
        name: t('navigation.dashboardShort'),
        href: withAppModePath('writer', '/dashboard'),
        icon: Squares2X2Icon,
        current: normalizedPath === '/dashboard' || normalizedPath === '/'
      },
      {
        name: t('navigation.content'),
        href: withAppModePath('writer', '/articles'),
        icon: DocumentTextIcon,
        current: normalizedPath === '/articles' || normalizedPath.startsWith('/articles/')
      },
      {
        name: t('navigation.podcasts'),
        href: withAppModePath('writer', '/podcasts'),
        icon: MicrophoneIcon,
        current: normalizedPath.startsWith('/podcasts')
      },
      {
        name: t('navigation.datasets'),
        href: withAppModePath('writer', '/datasets'),
        icon: CircleStackIcon,
        current: normalizedPath.startsWith('/datasets')
      }
    ];
  }, [activeAppMode, normalizedPath, t]);

  const userNavigation = useMemo(() => [
    { name: t('auth.signOut'), href: '#', onClick: handleSignOut },
  ], [t, handleSignOut]);

  const calculateDaysRemaining = () => {
    if (!trialEnds) return null;
    const today = new Date();
    const timeDiff = trialEnds.getTime() - today.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysRemaining > 0 ? daysRemaining : 0;
  };

  const renderTrialBanner = () => {
    const daysRemaining = calculateDaysRemaining();

    if (daysRemaining === null) return null;

    if (daysRemaining > 0) {
      return (
        <div className="bg-primary-500 fixed top-0 left-0 right-0 z-[100]" style={{ height: '48px' }}>
          <div className="h-full flex items-center justify-center lg:pl-[75px]">
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="flex items-center">
                <span className="flex rounded-lg p-2">
                  <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <p className="ml-3 font-medium text-white text-sm">
                  <span className="block sm:hidden">
                    {t('trial.daysLeft', { count: daysRemaining })}
                  </span>
                  <span className="hidden sm:block">
                    {t('trial.daysLeftLong', { count: daysRemaining })}
                  </span>
                </p>
              </div>
              <a
                href="/profile#subscription"
                className="flex items-center justify-center rounded-md border border-transparent bg-white px-4 py-1 text-xs font-medium text-primary-500 shadow-sm hover:bg-primary-50"
              >
                {t('trial.upgradeNow')}
              </a>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="bg-indigo-600 fixed top-0 left-0 right-0 z-[100]" style={{ height: '48px' }}>
          <div className="h-full flex items-center justify-center lg:pl-[75px]">
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="flex items-center">
                <span className="flex rounded-lg bg-indigo-800 p-2">
                  <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <p className="ml-3 font-medium text-white text-sm">
                  {subscriptionStatus === 'active' ? (
                    <>
                      <span className="md:hidden">{t('trial.premiumActive')}</span>
                      <span className="hidden md:inline">{t('trial.onPlan', { plan: subscriptionPlan })}</span>
                    </>
                  ) : (
                    <>
                      <span className="md:hidden">{t('trial.ended')}</span>
                      <span className="hidden md:inline">{t('trial.endedLong')}</span>
                    </>
                  )}
                </p>
              </div>
              {subscriptionStatus !== 'active' && (
                <a
                  href="/profile#subscription"
                  className="flex items-center justify-center rounded-md border border-transparent bg-white px-4 py-2 text-sm font-medium text-indigo-600 shadow-sm hover:bg-indigo-50"
                >
                  {t('trial.purchasePlan')}
                </a>
              )}
            </div>
          </div>
        </div>
      );
    }
  };

  const trialBanner = renderTrialBanner();
  const trialBannerHeight = trialBanner ? 48 : 0;
  const conversationsDrawerWidth = 440;
  const notificationsDrawerWidth = 420;
  const isProfileRoute = location.pathname === '/profile';
  const isAppChooserRoute = normalizedPath === '/' && !modeFromPath;
  const isAdminRoute = isAdminPath(location.pathname);
  const shouldShowSidebar = !isProfileRoute && !isAppChooserRoute && !isAdminRoute;
  const isFluidRoute = useMemo(() => {
    // Keep fluid layout only where full-width workspace is intentional.
    if (normalizedPath.startsWith('/articles/create-article')) return true;
    if (normalizedPath.startsWith('/articles/generate-draft')) return true;
    if (/^\/articles\/[^/]+/.test(normalizedPath)) return true; // article editor/details
    if (normalizedPath.startsWith('/chat')) return true;
    if (normalizedPath.startsWith('/conversations')) return true;
    if (normalizedPath.startsWith('/assistants')) return true;
    if (normalizedPath.startsWith('/ai-agents')) return true;
    if (normalizedPath.startsWith('/ai-assistant')) return true;
    if (normalizedPath.startsWith('/datasets/create-dataset')) return true;
    if (normalizedPath.startsWith('/datasets/details/')) return true;
    if (/^\/podcasts\/[^/]+/.test(normalizedPath)) return true; // podcast create/details
    if (normalizedPath.startsWith('/profile')) return true;
    if (isAdminPath(normalizedPath)) return true;
    return false;
  }, [normalizedPath]);
  const mainContentContainerClass = isFluidRoute
    ? 'w-full'
    : 'mx-auto w-full max-w-7xl';
  const accountShellValue = useMemo(() => ({
    userNavigation,
    onSignOut: handleSignOut,
    isNotificationsDrawerOpen,
    onToggleNotificationsDrawer: () => {
      setIsNotificationsDrawerOpen((prev) => !prev);
      setIsConversationsDrawerOpen(false);
    }
  }), [userNavigation, handleSignOut, isNotificationsDrawerOpen]);
  const activeRightDrawerWidth = isNotificationsDrawerOpen
    ? notificationsDrawerWidth
    : (isConversationsDrawerOpen ? conversationsDrawerWidth : 0);

  // Don't render until i18n is ready
  if (!ready) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
    </div>;
  }

  return (
    <ChangelogProvider>
      <div className="bg-neutral-100 min-h-screen top-0">
        {isAuthenticated ? (
          <>
            {trialBanner}
            {shouldShowSidebar && (
              <MobileSidebarToggle
                onOpen={() => setSidebarOpen(true)}
                trialBannerHeight={trialBannerHeight}
              />
            )}
            {!shouldShowSidebar && (
              <AccountHeader
                trialBannerHeight={trialBannerHeight}
                userNavigation={userNavigation}
                onSignOut={handleSignOut}
                className={isProfileRoute ? 'lg:hidden' : ''}
                isNotificationsDrawerOpen={isNotificationsDrawerOpen}
                onToggleNotificationsDrawer={accountShellValue.onToggleNotificationsDrawer}
              />
            )}
            {shouldShowSidebar && (
              <Sidebar
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                navigation={navigation}
                classNames={classNames}
                teams={teams}
                Cog6ToothIcon={Cog6ToothIcon}
                userNavigation={userNavigation}
                trialBannerHeight={trialBannerHeight}
                appLauncherLabel={t('navigation.apps')}
                activeAppMode={activeAppMode}
                isNotificationsDrawerOpen={isNotificationsDrawerOpen}
                onToggleNotificationsDrawer={() => {
                  setIsNotificationsDrawerOpen((prev) => !prev);
                  setIsConversationsDrawerOpen(false);
                }}
                onSignOut={handleSignOut}
              />
            )}
            <AccountShellProvider value={accountShellValue}>
            <main
              className={`bg-neutral-100 transition-[margin-right] duration-300 ease-in-out ${
                shouldShowSidebar
                  ? 'max-lg:[padding-top:calc(var(--app-top-offset)+3rem)] lg:[padding-top:var(--app-top-offset)]'
                  : isProfileRoute
                    ? 'max-lg:[padding-top:calc(var(--app-top-offset)+3rem)] lg:[padding-top:var(--app-top-offset)]'
                    : '[padding-top:calc(var(--app-top-offset)+3rem)]'
              } ${(isProfileRoute || isAppChooserRoute || isAdminRoute) ? '' : 'lg:pl-[75px]'}`}
              style={{
                '--app-top-offset': `${trialBannerHeight}px`,
                marginRight: `${activeRightDrawerWidth}px`
              }}
            >
                <div className={mainContentContainerClass}>
                <AppRoutes
                  trialEnded={trialEnded}
                  userFirstName={userFirstName}
                  handleSignIn={handleSignIn}
                  activeAppMode={activeAppMode}
                  withAppModePath={withAppModePath}
                />
              </div>
            </main>
            </AccountShellProvider>
            {!isAppChooserRoute && (
              <aside
                className={`fixed right-0 z-30 border-l border-neutral-200 bg-neutral-100 p-3 transition-transform duration-300 ease-in-out ${
                  isConversationsDrawerOpen && !isNotificationsDrawerOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'
                } ${
                  shouldShowSidebar
                    ? 'max-lg:top-[calc(var(--app-top-offset)+3rem)] lg:top-[var(--app-top-offset)] max-lg:h-[calc(100vh-var(--app-top-offset)-3rem)] lg:h-[calc(100vh-var(--app-top-offset))]'
                    : 'top-[calc(var(--app-top-offset)+3rem)] h-[calc(100vh-var(--app-top-offset)-3rem)]'
                }`}
                style={{
                  '--app-top-offset': `${trialBannerHeight}px`,
                  width: `${conversationsDrawerWidth}px`
                }}
              >
                <div className="h-full">
                  {isConversationsDrawerOpen && !isNotificationsDrawerOpen ? (
                    <WidgetConversationsChat />
                  ) : null}
                </div>
              </aside>
            )}
            {!isAppChooserRoute && (
              <aside
                className={`fixed right-0 z-30 border-l border-neutral-200 bg-white transition-transform duration-300 ease-in-out ${
                  isNotificationsDrawerOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'
                } ${
                  shouldShowSidebar
                    ? 'max-lg:top-[calc(var(--app-top-offset)+3rem)] lg:top-[var(--app-top-offset)] max-lg:h-[calc(100vh-var(--app-top-offset)-3rem)] lg:h-[calc(100vh-var(--app-top-offset))]'
                    : 'top-[calc(var(--app-top-offset)+3rem)] h-[calc(100vh-var(--app-top-offset)-3rem)]'
                }`}
                style={{
                  '--app-top-offset': `${trialBannerHeight}px`,
                  width: `${notificationsDrawerWidth}px`
                }}
              >
                <NotificationsDrawerContent onClose={() => setIsNotificationsDrawerOpen(false)} />
              </aside>
            )}
        </>
      ) : location.pathname === '/signup' ? (
        <SignUp onSignIn={handleSignIn} />
      ) : location.pathname === '/forgot-password' ? (
        <ForgotPassword />
      ) : location.pathname === '/reset-password' ? (
        <ResetPassword />
      ) : location.pathname === '/security' ? (
        <TrustPage />
      ) : (
        <SignIn onSignIn={handleSignIn} />
      )}
      </div>
    </ChangelogProvider>
  );
}